import React, { useEffect } from 'react';
import { Route, Switch, BrowserRouter, Redirect } from 'react-router-dom';
import IntersectionTable from '../components/Reporting/Intersection/IntersectionTable';
import InactiveUsersTable from '../components/Reporting/InactiveUsers/InactiveUsersTable';
import AllUserAccsTable from '../components/Reporting/AllUserAccounts/AllUserAccsTable';
import UsersWithoutRolesTable from '../components/Reporting/UsersWithoutRoles/UsersWithoutRolesTable';
import PgChangesTable from '../components/Reporting/PGsChangePerUser/PgChangesTable';
import UnmatchedEmployeesTable from '../components/Reporting/UnmatchedEmployees/UnmatchedEmployees';
import Header from '../components/Header/header';
import Footer from '../components/Footer/footer';
import './layout.css';
import Message from '../components/Message/message';
import { observer, inject } from 'mobx-react';
import NoPermissionsRedirect from '../components/RedirectPages/NoPermissionsRedirect';
import QarSystems from '../components/QarSystems/QarSystems';
import { initialPath } from '../helper/constants';
import GCP from '../components/Reporting/GCP/GCP';
import PGsWithoutDescription from '../components/Reporting/PGsWithoutDescription/PGsWithoutDescription';
import PasswordNeverExpiresUsers from '../components/Reporting/PasswordNeverExpiresUsers/PasswordNeverExpiresUsers';
import QARFindings from '../components/Reporting/QARFindings/QARFindings';
import { QARForm } from '../pages/QARForm';
import { LicenseAdmin } from '../pages/LicenseAdmin';
import { LicenseForms } from '../pages/LicenseForms';

const Layout = inject('RootStore')(observer(({ RootStore }) => {

    useEffect(() => {
        if (!localStorage.getItem(initialPath) && window.location.pathname !== '/') {
            localStorage.setItem(initialPath, window.location.pathname);
        }
        if (RootStore.userStore.currentUser === undefined) {
            RootStore.userStore.getCurrentUserInfo();
        }
    }, [RootStore.userStore]);

    useEffect(() => {
        RootStore.accountStore.getAccountsSyncStatus();
    }, [RootStore.accountStore, RootStore.userStore.currentUser]);

    const user = RootStore.userStore.currentUser;
    const isLoggedIn = user !== undefined;

    const protectedRoute = (Component) => {
        return isLoggedIn ? <Component /> : (window.location.pathname = '/login');
    };

    const reportingRoutes = [
        { path: '/intersection-report', component: () => protectedRoute(IntersectionTable) },
        { path: '/users-without-roles-report', component: () => protectedRoute(UsersWithoutRolesTable) },
        { path: '/inactive-user-accounts-report', component: () => protectedRoute(InactiveUsersTable) },
        { path: '/all-user-accounts-report', component: () => protectedRoute(AllUserAccsTable) },
        { path: '/permission-group-changes-report', component: () => protectedRoute(PgChangesTable) },
        { path: '/unrecognized-users', component: () => protectedRoute(UnmatchedEmployeesTable) },
        { path: '/gcp-report', component: () => protectedRoute(GCP) },
        { path: '/pgs-without-descriptions-report', component: () => protectedRoute(PGsWithoutDescription) },
        { path: '/password-never-expires-report', component: () => protectedRoute(PasswordNeverExpiresUsers) },
        { path: '/qar-findings', component: () => protectedRoute(QARFindings) },
    ];

    const adminRoutes = [
        { path: '/', component: () => <Redirect to="/inactive-user-accounts-report" /> },
        { path: '/qar-forms', component: () => <QARForm /> },
        { path: '/qar-admin', component: () => <QarSystems /> },
        ...reportingRoutes
    ];

    const soxComplianceRoutes = [
        { path: '/', component: () => <Redirect to="/qar-admin" /> },
        { path: '/qar-admin', component: () => <QarSystems /> },
        { path: '/qar-forms', component: () => <QARForm /> },
        ...reportingRoutes
    ];

    const managerRoutes = [
        { path: '/', component: () => <Redirect to="/qar-forms" /> },
        { path: '/qar-forms', component: () => <QARForm /> },
        { path: '/all-user-accounts-report', component: () => protectedRoute(AllUserAccsTable) },
        ...reportingRoutes
    ];

    const licenseAdminRoutes = [
        { path: '/', component: () => <Redirect to="/license-admin" /> },
        { path: '/license-forms', component: () => <LicenseForms /> },
        { path: '/license-admin', component: () => <LicenseAdmin /> },
    ];

    const licenseManagerRoutes = [
        { path: '/', component: () => <Redirect to="/license-forms" /> },
        { path: '/license-forms', component: () => <LicenseForms /> },
    ];

    const reportingUserRoutes = [
        { path: '/', component: () => <Redirect to="/inactive-user-accounts-report" /> },
        ...reportingRoutes
    ];

    const getRoutesForGroups = () => {
        let combinedRoutes = [];

        if (RootStore.userStore.isUserAdmin) {
            combinedRoutes = combinedRoutes.concat(adminRoutes);
        }
        if (RootStore.userStore.isUserLicenseAdmin) {
            combinedRoutes = combinedRoutes.concat(licenseAdminRoutes);
        }
        if (RootStore.userStore.isUserSoxCompliance) {
            combinedRoutes = combinedRoutes.concat(soxComplianceRoutes);
        }
        if (RootStore.userStore.isUserManager) {
            combinedRoutes = combinedRoutes.concat(managerRoutes);
        }
        if (RootStore.userStore.isUserLicenseManager) {
            combinedRoutes = combinedRoutes.concat(licenseManagerRoutes);
        }
        if (RootStore.userStore.isReportingUser) {
            combinedRoutes = combinedRoutes.concat(reportingUserRoutes);
        }

        const uniqueRoutesMap = new Map();
        combinedRoutes.forEach(r => {
            if (!uniqueRoutesMap.has(r.path)) {
                uniqueRoutesMap.set(r.path, r);
            }
        });

        return Array.from(uniqueRoutesMap.values());
    };

    const renderRoutes = () => {
        const userRoutes = getRoutesForGroups();

        if (userRoutes.length === 0) {
            return <NoPermissionsRedirect />;
        }

        return (
            <Switch>
                {userRoutes.map((route, index) => (
                    <Route
                        key={index}
                        exact
                        path={route.path}
                        component={route.component}
                    />
                ))}
            </Switch>
        );
    };

    return (
        <>
            <Message />
            <BrowserRouter>
                {RootStore.userStore.isUserPresent ? <div className='header'><Header /></div> : null}
                <div className="main-layout">
                    {RootStore.userStore.currentUser !== undefined ?
                        <>
                            {renderRoutes()}
                        </>
                        : null}

                </div>
            </BrowserRouter>
            <Footer />
        </>
    );
}));

export default Layout;
